import { DateRangePicker, MonthPicker, Toggle } from '@/components_deprecated'
import { EXPORT_SALARY_CORRECTIONS } from '@/modules/exports/mutations'
import { handleDateRangeChange } from '@/utils/date/handleDateRangeChange'
import { useOnErrorAuto } from '@/utils/hooks'
import { isInvalidDate } from '@/utils/time'
import { useMutation } from '@apollo/client'
import { ComponentSwitch, Icon, useToast } from '@ur-solutions/react-components'
import { useForm, useTranslate } from '@ur-solutions/react-hooks'
import { endOfMonth, formatISO, startOfMonth } from 'date-fns'
import React, { useMemo } from 'react'
import { isMobile } from 'react-device-detect'
import type { FormButton } from '../common'
import { Field, Form, Section } from '../common'

interface SalaryCorrectionsExportForm {
  month: Date
  dateStart: Date
  dateEnd: Date
  useDateRange: boolean
}

/**
 * The corrections panel on the work period overview shows one driver at a time;
 * this exports the same two lists -- manual salary corrections and rejected
 * activities -- for every driver in the company.
 */
export const ExportSalaryCorrectionsCard: React.FC = () => {
  const translations = useTranslate({
    header: 'settings.corrections-export',
    period: 'common.period',
    export: 'common.export',
    exportStarted: 'exports.success-user-exports',
    exportTimeout: 'exports.export-timeout',
    error: 'error.generic-server-error',
  })

  const addToast = useToast()
  const onErrorAuto = useOnErrorAuto()

  const [doExport, { loading }] = useMutation(EXPORT_SALARY_CORRECTIONS, {
    onCompleted(data) {
      if (data.exportSalaryCorrections?.ok) {
        addToast('success', translations.exportStarted)
      } else if (data.exportSalaryCorrections?.exportTimeout) {
        addToast('info', translations.exportTimeout)
      } else {
        addToast('error', translations.error)
      }
    },
    onError: onErrorAuto(translations.error),
  })

  const {
    formValues: form,
    formChangeHandler: handler,
    updateForm,
  } = useForm<SalaryCorrectionsExportForm>({
    values: {
      month: new Date(),
      dateStart: new Date(),
      dateEnd: new Date(),
      useDateRange: false,
    },
    config: {
      storage: {
        storeFormState: true,
        retrieveFormStateOnMount: true,
        storeFormStateName: 'Export-salary-corrections-form',
        transformValues: {
          month: value =>
            typeof value === 'string'
              ? isInvalidDate(new Date(value), new Date())
              : undefined,
          dateStart: value =>
            typeof value === 'string'
              ? isInvalidDate(new Date(value), new Date())
              : undefined,
          dateEnd: value =>
            typeof value === 'string'
              ? isInvalidDate(new Date(value), new Date())
              : undefined,
          useDateRange: value => value as boolean,
        },
      },
    },
  })

  const formButtons = useMemo<FormButton[]>(
    () => [
      {
        text: (
          <>
            <Icon
              icon="download"
              type="light"
              color="primary"
              margin="0 0.5ch 0 0"
            />
            {translations.export}
          </>
        ),
        type: 'submit',
        buttonProps: {
          fullWidth: isMobile,
          disabled: loading,
        },
        onClick: () => {
          const [dateStart, dateEnd] = form.useDateRange
            ? [form.dateStart, form.dateEnd]
            : [startOfMonth(form.month), endOfMonth(form.month)]

          void doExport({
            variables: {
              dateStart: formatISO(dateStart),
              dateEnd: formatISO(dateEnd),
            },
          })
        },
      },
    ],
    [
      doExport,
      form.dateEnd,
      form.dateStart,
      form.month,
      form.useDateRange,
      loading,
      translations.export,
    ]
  )

  return (
    <Section
      header={translations.header}
      loading={loading}
      actions={
        <Toggle
          value={form.useDateRange}
          thumbBackground="secondary"
          iconMode="svg"
          localOnIconProps={{
            icon: 'calendar-selection',
            fill: form.useDateRange ? 'white' : 'primary',
          }}
          localOffIconProps={{
            icon: 'calendar-month',
            fill: !form.useDateRange ? 'white' : 'primary',
          }}
          onChange={() => handler('useDateRange')(!form.useDateRange)}
        />
      }
    >
      <Form
        grid={{
          columns: !isMobile ? '1fr 2fr 2fr' : '1fr',
          areas: `'month month month'`,
        }}
        buttons={formButtons}
      >
        <Field area="month">
          <label>{translations.period}</label>
          <ComponentSwitch>
            <ComponentSwitch.Case case={form.useDateRange}>
              <DateRangePicker
                value={[form.dateStart, form.dateEnd]}
                grayed={false}
                onChange={handleDateRangeChange(updateForm)}
              />
            </ComponentSwitch.Case>

            <ComponentSwitch.Case default>
              <MonthPicker
                value={form.month}
                light
                futureYears={0}
                noYearCycle
                onChange={handler('month')}
              />
            </ComponentSwitch.Case>
          </ComponentSwitch>
        </Field>
      </Form>
    </Section>
  )
}
